// Utilidades de app instalable (PWA) y notificaciones push.
import api from './api';

export const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isAndroid = () => /android/i.test(navigator.userAgent);
export const isMobile = () => isIOS() || isAndroid() || window.matchMedia('(pointer: coarse) and (max-width: 900px)').matches;
/* Qué navegador es, para explicar la instalación donde de verdad está el botón.

   El orden importa. En el iPhone todos los navegadores corren sobre WebKit y
   arrastran "Safari" en el user agent, así que los de terceros hay que
   reconocerlos por su propia marca —CriOS, FxiOS, EdgiOS— ANTES de dar por
   sentado que es Safari. Si no, a alguien en Chrome se le explica cómo hacerlo
   en Safari, que fue justo lo que pasaba.

   En Android pasa parecido: el navegador de Samsung también dice "Chrome". */
export function navegador() {
  const ua = navigator.userAgent;
  if (/CriOS/i.test(ua)) return 'chrome-ios';
  if (/FxiOS/i.test(ua)) return 'firefox-ios';
  if (/EdgiOS/i.test(ua)) return 'edge-ios';
  if (isIOS()) return 'safari-ios';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/EdgA|Edge?\//i.test(ua)) return 'edge';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/Chrome|Chromium/i.test(ua)) return 'chrome';
  return 'otro';
}

/* Los pasos para instalar, según dónde esté la persona.

   Desde iOS 16.4 todos los navegadores del iPhone pueden instalar desde
   Compartir; lo único que cambia es dónde está ese botón. En Android, en
   cambio, cada uno lo llama distinto y está en su propio menú. */
const PASOS = {
  'safari-ios':  { nombre: 'Safari',  pasos: ['Compartir ⬆️ (abajo)', 'Agregar a inicio'] },
  'chrome-ios':  { nombre: 'Chrome',  pasos: ['⋮ (arriba a la derecha)', 'Compartir', 'Agregar a pantalla de inicio'] },
  'edge-ios':    { nombre: 'Edge',    pasos: ['⋯ (abajo)', 'Compartir', 'Agregar a pantalla de inicio'] },
  'firefox-ios': { nombre: 'Firefox', pasos: ['⋯ (abajo)', 'Compartir', 'Agregar a pantalla de inicio'] },
  chrome:        { nombre: 'Chrome',  pasos: ['⋮ (arriba a la derecha)', 'Instalar aplicación'] },
  samsung:       { nombre: 'Samsung Internet', pasos: ['☰ (abajo)', 'Agregar página a', 'Pantalla de inicio'] },
  firefox:       { nombre: 'Firefox', pasos: ['⋮ (arriba a la derecha)', 'Instalar'] },
  edge:          { nombre: 'Edge',    pasos: ['⋯ (abajo)', 'Agregar al teléfono'] },
  otro:          { nombre: null,      pasos: ['el menú del navegador', '"Instalar app" o "Agregar a pantalla de inicio"'] },
};

export function comoInstalar() {
  const cual = navegador();
  const base = PASOS[cual] || PASOS.otro;
  // En iPhone la instalación existe recién desde iOS 16.4: si no aparece, es eso
  return { ...base, nota: isIOS() ? 'Necesitás iOS 16.4 o más nuevo.' : null };
}

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
// En iPhone las push solo funcionan con la app agregada al inicio
export const pushNeedsInstall = () => isIOS() && !isStandalone();
export const permission = () => ('Notification' in window ? Notification.permission : 'unsupported');

// Evento de instalación de Android/Chrome (se captura lo antes posible)
let deferredInstall = null;
const listeners = new Set();
window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredInstall = e; listeners.forEach(fn => fn(true)); });
window.addEventListener('appinstalled', () => { deferredInstall = null; listeners.forEach(fn => fn(false)); });
export const canPromptInstall = () => !!deferredInstall;
export const onInstallAvailable = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export async function promptInstall() {
  if (!deferredInstall) return false;
  deferredInstall.prompt();
  const choice = await deferredInstall.userChoice.catch(() => null);
  deferredInstall = null;
  return choice && choice.outcome === 'accepted';
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => {}); });
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// Pide permiso y registra este dispositivo. Devuelve 'granted' | 'denied' | 'default' | 'unsupported' | 'needs_install'
export async function enablePush() {
  if (pushNeedsInstall()) return 'needs_install';
  if (!pushSupported()) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm;
  const { data } = await api.get('/notifications/settings');
  const key = data.availability && data.availability.push && data.availability.push.public_key;
  if (!key) return 'unsupported';
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  await api.post('/notifications/push/subscribe', { subscription: sub.toJSON() });
  return 'granted';
}

export async function isPushActiveHere() {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && await reg.pushManager.getSubscription();
  return !!sub;
}

export async function disablePushHere() {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && await reg.pushManager.getSubscription();
  if (sub) { await api.post('/notifications/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {}); await sub.unsubscribe(); }
}
